import { supabase } from "../supabase";
import type { OrgRole } from "./types";

// ---------------------------------------------------------------------------
// Invite tokens.
//
// The raw token only ever appears in the email link the invitee receives.
// Our database stores the SHA-256 of the raw token (`token_hash`), so a read
// of `org_invitations` never exposes a value that could be used to accept
// invitations (the accept RPC takes the hash as input and validates it
// against `auth.jwt() ->> 'email'`). That keeps the threat surface small:
//
//   - Someone with admin DB read can't impersonate invitees.
//   - The raw token is single-use by design — once accepted_at is set the
//     RPC rejects further calls with code P7403.
//   - The hash is the URL's only secret, so it must be 32 bytes of CSPRNG
//     output (not a UUID). We base64url-encode so the URL-safe form can
//     travel in a query param without escaping.
// ---------------------------------------------------------------------------

// 7-day expiry — matches the `default (now() + interval '7 days')` the
// migration sets, but we compute client-side too so the UI can show the
// right "expires" label immediately without a round-trip.
export const INVITE_EXPIRY_DAYS = 7;

/** URL-safe base64 (RFC 4648 §5, no padding). */
function base64UrlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i]);
  }
  const b64 = btoa(bin);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Lowercase hex encoding of the buffer. */
function toHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}

/**
 * SHA-256 of the raw token, as lowercase hex. Exported so the accept flow
 * in AuthCallback can re-derive the hash from the URL token without
 * duplicating the crypto-subtle dance.
 */
export async function hashInviteToken(raw: string): Promise<string> {
  const enc = new TextEncoder();
  const digest = await crypto.subtle.digest("SHA-256", enc.encode(raw));
  return toHex(new Uint8Array(digest));
}

/**
 * Generate a fresh invite token. Returns both the raw (email-safe) value
 * and the hash that gets persisted to `org_invitations.token_hash`.
 */
export async function generateInviteToken(): Promise<{
  raw: string;
  hash: string;
}> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const raw = base64UrlEncode(bytes);
  const hash = await hashInviteToken(raw);
  return { raw, hash };
}

export interface SubmitInvitationInput {
  email: string;
  role: OrgRole;
  orgId: string;
  inviterId: string;
}

export interface SubmitInvitationResult {
  raw: string;
  hash: string;
  url: string;
}

/**
 * Insert an `org_invitations` row and trigger Supabase's magic-link send.
 * Returns the full invite URL for admin copy/paste fallback in case email
 * delivery fails.
 */
export async function submitInvitation(
  input: SubmitInvitationInput,
): Promise<SubmitInvitationResult> {
  const email = input.email.trim().toLowerCase();
  if (!email) throw new Error("Email is required");

  const { raw, hash } = await generateInviteToken();
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://proofly.one";
  const url = `${origin}/auth/callback?invite=${raw}`;

  const { error: insertError } = await supabase.from("org_invitations").insert({
    organization_id: input.orgId,
    email,
    role: input.role,
    token_hash: hash,
    invited_by: input.inviterId,
  });
  if (insertError) {
    throw new Error(insertError.message);
  }

  // Fire-and-wait on the magic-link send. If it fails we still have the row
  // and a URL the admin can deliver manually.
  const { error: otpError } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
      emailRedirectTo: url,
    },
  });
  if (otpError) {
    // Don't throw — the invite row is valid and the URL will still work.
    // eslint-disable-next-line no-console
    console.warn(`[invites] OTP send failed: ${otpError.message}`);
  }

  return { raw, hash, url };
}
